'use client';

import { useState, useTransition } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Flag } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { analytics } from '@kit/analytics';
import { Button } from '@kit/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@kit/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kit/ui/select';
import { toast } from '@kit/ui/sonner';
import { Textarea } from '@kit/ui/textarea';
import { Trans } from '@kit/ui/trans';

import { reportListingAction } from '../_lib/server/report-listing.action';

const REASON_OPTIONS: ReadonlyArray<{
  value:
    | 'counterfeit'
    | 'offensive'
    | 'wrong_category'
    | 'spam'
    | 'off_platform_solicitation'
    | 'other';
  labelKey: string;
}> = [
  { value: 'counterfeit', labelKey: 'explore:report.reasons.counterfeit' },
  { value: 'offensive', labelKey: 'explore:report.reasons.offensive' },
  { value: 'wrong_category', labelKey: 'explore:report.reasons.wrongCategory' },
  { value: 'spam', labelKey: 'explore:report.reasons.spam' },
  {
    value: 'off_platform_solicitation',
    labelKey: 'explore:report.reasons.offPlatform',
  },
  { value: 'other', labelKey: 'explore:report.reasons.other' },
];

const FormSchema = z.object({
  reason: z.enum([
    'counterfeit',
    'offensive',
    'wrong_category',
    'spam',
    'off_platform_solicitation',
    'other',
  ]),
  details: z.string().max(1000).optional(),
});

type FormInput = z.infer<typeof FormSchema>;

interface ReportListingDialogProps {
  /** Set when reporting a listing directly (marketplace detail). */
  listingId?: string;
  /** Set when reporting a problem with an order (purchases / sales detail). */
  orderId?: string;
  /** Optional trigger override — defaults to a small ghost "Report" button. */
  trigger?: React.ReactNode;
}

/**
 * Generic report dialog. Used in two surfaces:
 *  - Marketplace listing detail page (`listingId` passed)
 *  - Order detail pages — purchases/[id] and sales/[id] (`orderId` passed)
 *
 * Both surfaces share the same Zod schema in the server action; either
 * `listingId` or `orderId` is required (DB CHECK + action refine).
 */
export function ReportListingDialog({
  listingId,
  orderId,
  trigger,
}: ReportListingDialogProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [isPending, startTransition] = useTransition();

  const form = useForm<FormInput>({
    resolver: zodResolver(FormSchema),
    // Default to 'other' rather than 'spam' to avoid biasing reports toward
    // any single category (PR #763 review finding #4, reverted from the
    // empty-sentinel approach because it tripped @hookform/resolvers vs
    // react-hook-form generic-parameter type mismatches at build time).
    defaultValues: {
      reason: 'other',
      details: '',
    },
  });

  function onSubmit(values: FormInput) {
    startTransition(async () => {
      const result = await reportListingAction({
        listingId,
        orderId,
        reason: values.reason,
        details: values.details,
      });

      if (result.success) {
        // PostHog: report-listing — fired only on successful submission.
        // No PII; we omit `details` (free-text user input) and only carry
        // the listing id (or order id) and the categorical reason.
        void analytics.trackEvent('report-listing', {
          listing_id: listingId ?? '',
          order_id: orderId ?? '',
          reason: values.reason,
        });
        toast.success(t('explore:report.submitted'), {
          description: t('explore:report.submittedDescription'),
        });
        form.reset();
        setOpen(false);
      } else {
        toast.error(t('explore:report.submitError'), {
          description: result.error,
        });
      }
    });
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-foreground gap-1.5"
          >
            <Flag className="h-3.5 w-3.5" aria-hidden />
            <Trans i18nKey="explore:report.reportListing" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Trans i18nKey="explore:report.title" />
          </DialogTitle>
          <DialogDescription>
            <Trans i18nKey="explore:report.dialogDescription" />
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
            aria-busy={isPending}
          >
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Trans i18nKey="explore:report.reason" />
                  </FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={isPending}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t('explore:report.reasonPlaceholder')}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {REASON_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {t(opt.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="details"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Trans i18nKey="explore:report.details" />
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('explore:report.detailsPlaceholder')}
                      maxLength={1000}
                      rows={4}
                      disabled={isPending}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={isPending}
              >
                <Trans i18nKey="common:cancel" />
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? t('explore:report.submitting')
                  : t('explore:report.submitReport')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
